/**
 * Pre-mount WebGL probe (MATTOBOARD-3V4). Must not import `three` — this runs on
 * the eager App path. Fails OPEN so a bad probe never blacks out a healthy user.
 */
export function detectWebGLSupport(
  createCanvas: () => HTMLCanvasElement = () =>
    document.createElement("canvas"),
): boolean {
  try {
    const canvas = createCanvas();
    if (typeof canvas?.getContext !== "function") return true;

    // WebGL2 only: three r163+ refuses a WebGL1 context, so a WebGL1-only
    // client would pass the probe and still crash in the editor.
    const ctx = canvas.getContext("webgl2") as WebGL2RenderingContext | null;
    if (!ctx) return false;

    // Release the probe context — browsers cap ~16 live contexts per page.
    ctx.getExtension?.("WEBGL_lose_context")?.loseContext?.();
    return true;
  } catch {
    return true;
  }
}

let supported: boolean | null = null;

/**
 * Only a positive result is cached. A success consumed a context, so repeating
 * it costs something; a failure consumed nothing, and caching it would strand a
 * user whose probe failed transiently (GPU process down, memory pressure) in a
 * gate with no Reload button for the rest of the session.
 *
 * The genuine context-exhaustion population still probes true early, when
 * contexts are plentiful, so they keep the crash+Reload path where Reload
 * actually helps.
 */
export function isWebGLAvailable(): boolean {
  if (supported === true) return true;
  supported = detectWebGLSupport();
  return supported;
}
